import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { useApi } from "../../../../../http/api";
import {
  UpdateUprightTypeNewImageBody,
  UpdateUprightTypeRemoveImageBody,
  UprightType,
} from "../../../../../http/border/types/uprightTypes";
import { useUprightTypesApi } from "../../../../../http/border/useUprightTypes";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import { default as useImageUploader } from "../../../../ui/ImageUploader";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import TranslatableField from "../../../../ui/TranslatableField";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface UpdateUprightTypeDetailsProps {
  code: string | null;
  name: string | null;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
  order: number | null;
  visibility: VisibilityScopeValue;
}

interface UprightTypeDetailsProps {
  uprightType?: UprightType;
  disabled?: boolean;
}

const UprightTypeDetails: FC<UprightTypeDetailsProps> = ({ uprightType, disabled }: UprightTypeDetailsProps) => {
  const i18n = useI18n();
  const api = useApi();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const uprightTypesApi = useUprightTypesApi();

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: uprightType?.image,
    isLoading: !uprightType || (!!uprightType && !uprightType?.id),
    disabled,
  });

  const defaultValues: UpdateUprightTypeDetailsProps = {
    code: uprightType?.code ?? null,
    name: uprightType?.name ?? null,
    nameIt: uprightType?.nameIt ?? null,
    nameDe: uprightType?.nameDe ?? null,
    nameEs: uprightType?.nameEs ?? null,
    nameFr: uprightType?.nameFr ?? null,
    order: uprightType?.order ?? 50,
    visibility: uprightType?.visibility ?? "hidden",
  };

  const submit = async (body: UpdateUprightTypeNewImageBody | UpdateUprightTypeRemoveImageBody) => {
    if (uprightType) {
      try {
        await uprightTypesApi.updateAdminUprightType(uprightType.id, body);
        dispatch(showSuccessNotification(i18n.translation));
        onSave();
      } catch (err) {
        return dispatch(showErrorNotification(i18n, err));
      } finally {
        return setIsSubmitting(false);
      }
    }
  };

  const onSubmit = () => async (formData: UpdateUprightTypeDetailsProps) => {
    setIsSubmitting(true);

    const commonBody = {
      code: formData.code?.trim()!,
      name: formData.name?.trim()!,
      nameIt: formData.nameIt?.trim() ?? null,
      nameDe: formData.nameDe?.trim() ?? null,
      nameEs: formData.nameEs?.trim() ?? null,
      nameFr: formData.nameFr?.trim() ?? null,
      order: formData.order!,
      visibility: formData.visibility,
    };

    if (newImage && uprightType) {
      try {
        const signedUrl = await uprightTypesApi.generateUploadImageUrl(uprightType.id, newImage.name, newImage.type);
        const res = await api.uploadFile(signedUrl, newImage);
        if (!res.ok) {
          dispatch(showErrorNotification(i18n, res));
          setIsSubmitting(false);
          return;
        }
        const body: UpdateUprightTypeNewImageBody = {
          ...commonBody,
          image: newImage.name,
        };
        await submit(body);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      const body: UpdateUprightTypeRemoveImageBody = {
        ...commonBody,
        removeImage: removeImage,
      };
      await submit(body);
    }
  };

  return (
    <Form<UpdateUprightTypeDetailsProps>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting || disabled}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, control, setValue, reset, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [uprightType]);

        useEffect(() => {
          register("nameIt");
          register("nameDe");
          register("nameEs");
          register("nameFr");
        }, [register]);

        const values = watch();

        return (
          <FormFieldsContainer imageUploader={component}>
            <FormField label={i18n.translation.common.code} error={errors.code}>
              {({ labelId, isOptional, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled
                  forceUpper
                  {...register("code", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isOptional, isInvalid }) => (
                <TranslatableField
                  field="name"
                  formValues={values}
                  setFormValue={setValue}
                  dialogTitle={i18n.translation.common.name}
                >
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={disabled}
                    {...register("name", {
                      required: !isOptional,
                      minLength: 1,
                    })}
                  />
                </TranslatableField>
              )}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={disabled}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      disabled={disabled}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default UprightTypeDetails;
